import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { API_MESSAGING, API_SECONDARY_MESSAGING } from '../lib/constants';

import type { ActiveFlow } from '@avalara/types/index';
import type { SystemErrorOptions } from '@avalara/types/index';
import type { SystemState } from '@avalara/types/reducer-states';

const initialState: SystemState = {
  accountFetchFailed: false,
  activeFlow: null,
  error: null,
  isMobile: false,
  isPollingDialogOpen: false,
  inProgressFetches: {},
  needToFetchCybersourceData: true,
};

type FetchStatusPayload = {
  fetchName: string;
  inProgress: boolean;
};

const store = createSlice({
  name: 'system',
  initialState,
  reducers: {
    clearSystemError: (state) => {
      state.error = null;
    },

    setAccountFetchFailed: (state, action: PayloadAction<boolean>) => {
      state.accountFetchFailed = action.payload;
    },

    /** Resets system state to initial values, except activeFlow. */
    clearSystemState: (state) => ({ ...initialState, activeFlow: state.activeFlow }),

    openPollingDialog: (state, action: PayloadAction<string>) => {
      state.isPollingDialogOpen = true;
      state.provisioningStatusId = action.payload;
    },

    setActiveFlow: (state, action: PayloadAction<ActiveFlow>) => {
      state.activeFlow = action.payload;
    },

    setFetchStatus: (state, action: PayloadAction<FetchStatusPayload>) => {
      const { fetchName, inProgress } = action.payload;
      state.inProgressFetches[fetchName] = inProgress;
    },

    setIsMobile: (state, action: PayloadAction<boolean>) => {
      state.isMobile = action.payload;
    },

    setNeedToFetchCybersourceData: (state, action: PayloadAction<boolean>) => {
      state.needToFetchCybersourceData = action.payload;
    },

    setSystemError: (state, action: PayloadAction<SystemErrorOptions>) => {
      const { displaySimpleMessage, html, locationAfterErrorDismissal, serverMessage, title } = action.payload;

      let { message, secondaryMessage } = action.payload;

      // make sure message is not undefined. If so, make it empty string.
      // if not, add a space to ensure formatting when appending message details
      if (serverMessage !== null && serverMessage !== undefined && serverMessage !== '') {
        message = !message ? '' : `${message} `;
        const messageDetails = API_MESSAGING.find((a) => a.response === serverMessage) || { friendlyMessage: '' };
        const tmp = API_SECONDARY_MESSAGING.find((a) => a.response === serverMessage);
        secondaryMessage = tmp ? tmp.friendlyMessage : secondaryMessage;
        message = `${message}${messageDetails.friendlyMessage}`;
      }

      message = message.trim();

      state.error = {
        message,
        secondaryMessage,
        html,
        title,
        serverMessage,
        locationAfterErrorDismissal,
        displaySimpleMessage,
      };
    },
  },
});

export const {
  clearSystemError,
  clearSystemState,
  openPollingDialog,
  setAccountFetchFailed,
  setActiveFlow,
  setFetchStatus,
  setIsMobile,
  setNeedToFetchCybersourceData,
  setSystemError,
} = store.actions;

export default store;
